import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { RateReview as ReviewIcon } from '@mui/icons-material';
import type { TriggerRunResponse, RunSummary } from '../../api/scheduledJobs';
import { countFoundLatestNews } from './latestNewsUtils';

interface RunResultDialogProps {
  open: boolean;
  onClose: () => void;
  result: TriggerRunResponse | null;
  jobDisplayName: string;
  jobType?: string;
  isRunning?: boolean;
  onCancel?: () => void;
  /** Open batch review for Latest News Agent found PRs. */
  onReviewLatestNews?: () => void;
}

const RunResultDialog: React.FC<RunResultDialogProps> = ({
  open,
  onClose,
  result,
  jobDisplayName,
  jobType,
  isRunning = false,
  onCancel,
  onReviewLatestNews,
}) => {
  const status = result?.status;
  const runSummary: RunSummary | undefined =
    result?.run_summary ??
    (result as { runSummary?: RunSummary } | undefined)?.runSummary;
  const logLines = runSummary?.log_lines ?? [];
  const environmentUsed =
    (result?.environment_used ??
      (result as { environmentUsed?: string } | undefined)?.environmentUsed) ??
    'unknown';
  const runAt = result?.last_run_at
    ? new Date(result.last_run_at).toLocaleString()
    : null;

  const foundCount =
    jobType === 'latest_news_agent' ? countFoundLatestNews(runSummary) : 0;
  const canReview =
    !isRunning &&
    Boolean(onReviewLatestNews) &&
    foundCount > 0 &&
    (status === 'success' || status === 'partial');

  return (
    <Dialog
      open={open}
      onClose={isRunning ? () => {} : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Run Result: {jobDisplayName}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isRunning && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 1,
                py: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  {(() => {
                    const total = runSummary?.companies_total ?? 0;
                    const current = runSummary?.companies_current ?? 0;
                    if (total > 0) {
                      return current > 0
                        ? `Processing company ${current}/${total}`
                        : `Processing ${total} companies`;
                    }
                    return 'Running job…';
                  })()}
                </Typography>
              </Box>
              {runSummary?.log_lines && runSummary.log_lines.length > 0 && (
                <Box
                  component="pre"
                  sx={{
                    maxHeight: 200,
                    overflow: 'auto',
                    p: 1,
                    fontSize: '0.75rem',
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200',
                    borderRadius: 1,
                    width: '100%',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {runSummary.log_lines.join('\n')}
                </Box>
              )}
            </Box>
          )}
          {result && !isRunning && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Environment:
                </Typography>
                <Chip label={environmentUsed} size="small" variant="outlined" />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  Status:
                </Typography>
                <Chip
                  label={status}
                  size="small"
                  color={
                    status === 'success'
                      ? 'success'
                      : status === 'partial'
                        ? 'warning'
                        : status === 'cancelled'
                          ? 'default'
                          : 'error'
                  }
                  variant="filled"
                />
                {runAt && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    Run at: {runAt}
                  </Typography>
                )}
              </Box>

              {canReview && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                  }}
                >
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {foundCount} compan{foundCount === 1 ? 'y' : 'ies'} with
                    recent PRs
                    {typeof runSummary?.press_releases_found === 'number'
                      ? ` · ${runSummary.press_releases_found} PR(s)`
                      : ''}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                    Review and select which press releases to save to Neo4j.
                  </Typography>
                  <Button
                    variant="contained"
                    color="inherit"
                    size="small"
                    startIcon={<ReviewIcon />}
                    onClick={onReviewLatestNews}
                  >
                    Review found PRs
                  </Button>
                </Box>
              )}

              {result?.last_run_error && (
                <Box>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Error
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      p: 1,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    {result.last_run_error}
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Console output
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    maxHeight: 400,
                    overflow: 'auto',
                    p: 1.5,
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200',
                    color: (theme) =>
                      theme.palette.mode === 'dark' ? 'grey.100' : 'grey.900',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {logLines.length > 0
                    ? logLines.map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          {'\n'}
                        </React.Fragment>
                      ))
                    : `Run completed (no detailed log). Status: ${status}, Environment: ${environmentUsed}.`}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {isRunning && onCancel ? (
          <Button color="error" onClick={onCancel}>
            Cancel
          </Button>
        ) : (
          <>
            {canReview && (
              <Button
                variant="contained"
                color="warning"
                startIcon={<ReviewIcon />}
                onClick={onReviewLatestNews}
              >
                Review found PRs
              </Button>
            )}
            <Button onClick={onClose}>Close</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RunResultDialog;
